/**
 * What is left on screen after a call that never became one.
 *
 * "Call failed" in a toast tells nobody what to do next, and the two causes we
 * actually see — a microphone prompt nobody answered (usually in a background
 * tab, where Chrome does not even show it) and a network that will not let the
 * media through — need opposite fixes. So the failure stays up until it is
 * dismissed, says which of the two it was, and carries the tools to check both
 * from this very page: the microphone, the path to the relay servers the call
 * was given, and a report to hand to whoever runs the network.
 */
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  AlertTriangle,
  Check,
  ClipboardCopy,
  Mic,
  Phone,
  Wifi,
  X,
} from "lucide-react";
import type { ConnectivityCheck, MicCheck } from "calls-sdk";
import type { CallsSdkModule } from "./sdk";
import { detectBrowser, micHelpSteps } from "./browser-help";

export interface CallFailurePeer {
  userId: string;
  name: string;
}

export interface CallFailure {
  /** mic: the browser refused the microphone; mic-pending: our prompt was never
   *  answered; peer-permission: theirs was never answered; network: both sides
   *  had media and still found no path between them; dropped: the call WAS
   *  connected and the connection went away mid-call */
  kind: "mic" | "mic-pending" | "peer-permission" | "network" | "dropped";
  /** who to ring again; null when there is no one person (a group call) */
  peer: CallFailurePeer | null;
  companyId: string;
  conversationId: string;
  /** the STUN/TURN servers the failed call was given — the only honest thing
   *  to test the network against; null when it never got that far */
  iceServers: RTCIceServer[] | null;
  at: number;
}

// Below this the microphone opened but heard nothing at all: a hardware mute
// switch, or the wrong input device selected.
const SILENT_LEVEL = 0.02;
// TURN credentials are minted per call and short-lived; probing with expired
// ones would report a perfectly good network as blocked.
const ICE_SERVERS_FRESH_MS = 4 * 60_000;

/** Did the call service hand this call any relay (TURN) server at all? */
function turnOffered(iceServers: RTCIceServer[]): boolean {
  return iceServers.some((s) =>
    [s.urls].flat().some((u) => /^turns?:/i.test(u))
  );
}

/** Numbered "unblock the microphone" steps for the browser in front of us.
 *  `withReload={false}` while a call is still coming up — a reload then
 *  cancels it. */
export function MicHelpSteps({
  className = "",
  withReload = true,
}: {
  className?: string;
  withReload?: boolean;
}) {
  const { t } = useTranslation();
  const steps = useMemo(
    () => micHelpSteps(t, detectBrowser(), { withReload }),
    [t, withReload]
  );
  return (
    <ol className={`list-decimal space-y-0.5 pl-4 ${className}`}>
      {steps.map((s) => (
        <li key={s}>{s}</li>
      ))}
    </ol>
  );
}

type NetVerdict = "blocked" | "tls-only" | "no-relay" | "ok";

function netVerdict(r: ConnectivityCheck): NetVerdict {
  if (!r.stun && !r.relayAvailable) return "blocked";
  if (r.turnTls && !r.turnUdp && !r.turnTcp && !r.stun) return "tls-only";
  if (!r.relayAvailable) return "no-relay";
  return "ok";
}

/** Plain text for support: what failed, where, and what the checks found. */
function buildReport(
  failure: CallFailure,
  mic: MicCheck | null,
  net: ConnectivityCheck | null
): string {
  const browser = detectBrowser();
  const yes = (v: boolean) => (v ? "ok" : "FAIL");
  // the engine's own word once it has run; otherwise what the call was given
  const offered =
    net?.relayOffered === false
      ? false
      : failure.iceServers && failure.iceServers.length > 0
      ? turnOffered(failure.iceServers)
      : null;
  const lines = [
    "Call problem report",
    `Time: ${new Date().toISOString()}`,
    `Failed at: ${new Date(failure.at).toISOString()}`,
    `Problem: ${failure.kind}`,
    `Other side: ${failure.peer?.name || "-"}`,
    `Browser: ${browser.name} / ${browser.os}`,
    `User agent: ${typeof navigator !== "undefined" ? navigator.userAgent : "-"}`,
    `Microphone: ${
      mic
        ? mic.ok
          ? `ok, level ${mic.level.toFixed(2)}${
              mic.deviceLabel ? `, ${mic.deviceLabel}` : ""
            }`
          : mic.reason || "failed"
        : "not checked"
    }`,
    `TURN offered: ${offered === null ? "-" : offered ? "yes" : "no"}`,
    net
      ? `Network: STUN ${yes(net.stun)}, TURN UDP ${yes(
          net.turnUdp
        )}, TURN TCP ${yes(net.turnTcp)}, TURN TLS ${yes(net.turnTls)}`
      : "Network: not checked",
  ];
  return lines.join("\n");
}

export function CallFailurePanel({
  failure,
  getSdk,
  onCallAgain,
  onClose,
}: {
  failure: CallFailure;
  /** the engine, for its diagnostics; the checks themselves read it at click
   *  time, render only asks whether it has them at all */
  getSdk: () => CallsSdkModule | null;
  /** null hides the button: nobody to ring, or a call is already on */
  onCallAgain: (() => void) | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [micRunning, setMicRunning] = useState(false);
  const [mic, setMic] = useState<MicCheck | null>(null);
  const [netRunning, setNetRunning] = useState(false);
  const [net, setNet] = useState<ConnectivityCheck | null>(null);
  const [netUnavailable, setNetUnavailable] = useState(false);
  /** the call service gave this call no relay: nothing about the person's own
   *  network to test or to blame */
  const [noTurn, setNoTurn] = useState(false);

  const name = failure.peer?.name || t("call_the_other_side");
  const title =
    failure.kind === "mic"
      ? t("call_fail_mic_title")
      : failure.kind === "mic-pending"
      ? t("call_fail_mic_pending_title")
      : failure.kind === "peer-permission"
      ? t("call_fail_peer_title", { name })
      : failure.kind === "dropped"
      ? t("call_fail_dropped_title")
      : t("call_fail_network_title");
  const body =
    failure.kind === "mic"
      ? t("call_fail_mic_body")
      : failure.kind === "mic-pending"
      ? t("call_fail_mic_pending_body")
      : failure.kind === "peer-permission"
      ? t("call_fail_peer_body")
      : failure.kind === "dropped"
      ? t("call_fail_dropped_body")
      : t("call_fail_network_body");
  const ourMic = failure.kind === "mic" || failure.kind === "mic-pending";
  // An engine served before the diagnostics existed has neither check: a
  // button that can only ever say "failed" is worse than no button.
  const sdkNow = getSdk();
  const hasMicCheck = typeof sdkNow?.checkMicrophone === "function";
  const hasNetCheck = typeof sdkNow?.checkConnectivity === "function";
  // the other side's permission is theirs to fix, and a call that was already
  // talking had a working microphone; nothing here to check for either
  const canCheckMic =
    hasMicCheck &&
    failure.kind !== "peer-permission" &&
    failure.kind !== "dropped";
  const canCheckNet =
    hasNetCheck && (failure.kind === "network" || failure.kind === "dropped");

  const checkMic = async () => {
    const sdk = getSdk();
    if (!sdk || micRunning) return;
    setMicRunning(true);
    try {
      setMic(await sdk.checkMicrophone({ sampleMs: 1500 }));
    } catch {
      setMic({ ok: false, reason: "failed", level: 0 });
    } finally {
      setMicRunning(false);
    }
  };

  const checkNet = async () => {
    const sdk = getSdk();
    if (!sdk || netRunning) return;
    if (
      !failure.iceServers ||
      failure.iceServers.length === 0 ||
      Date.now() - failure.at > ICE_SERVERS_FRESH_MS
    ) {
      setNetUnavailable(true);
      return;
    }
    if (!turnOffered(failure.iceServers)) {
      setNoTurn(true);
      return;
    }
    setNetRunning(true);
    try {
      const res = await sdk.checkConnectivity(failure.iceServers);
      // the engine says itself whether any relay was on offer
      if (res.relayOffered === false) setNoTurn(true);
      setNet(res);
    } catch {
      // documented never to throw; if it does, nothing was reachable — and a
      // relay WAS there to try, or we would not have got past the check above
      setNet({
        stun: false,
        turnUdp: false,
        turnTcp: false,
        turnTls: false,
        relayAvailable: false,
        relayOffered: true,
      });
    } finally {
      setNetRunning(false);
    }
  };

  const copyReport = async () => {
    const text = buildReport(failure, mic, net);
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("call_report_copied"));
    } catch {
      // clipboard refused (no permission, insecure context): show the text
      // instead of pretending it was copied
      window.prompt(t("call_report"), text);
    }
  };

  const micLine = (() => {
    if (!mic) return null;
    if (mic.ok) {
      const works = mic.deviceLabel
        ? t("call_check_mic_ok_device", { device: mic.deviceLabel })
        : t("call_check_mic_ok");
      return mic.level < SILENT_LEVEL
        ? `${works} ${t("call_check_mic_silent")}`
        : works;
    }
    if (mic.reason === "denied") return t("call_check_mic_denied");
    if (mic.reason === "missing") return t("call_check_mic_missing");
    if (mic.reason === "busy") return t("call_check_mic_busy");
    return t("call_check_mic_failed");
  })();

  const verdict = net ? netVerdict(net) : null;
  const button =
    "flex items-center gap-1.5 rounded-lg bg-gray-700 px-2.5 py-1.5 text-xs text-gray-100 hover:bg-gray-600 disabled:opacity-50 max-md:min-h-11";

  return (
    <div
      role="alertdialog"
      aria-label={title}
      className="fixed bottom-[calc(var(--phone-bar-height,0px)+1rem)] right-4 z-[9999] max-h-[calc(100dvh-var(--phone-bar-height,0px)-2rem)] w-[340px] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-2xl bg-gray-800 p-3 text-gray-100 shadow-2xl"
    >
      <div className="mb-2 flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
        <span className="min-w-0 flex-1 text-sm font-medium">{title}</span>
      </div>
      <p className="text-xs text-gray-300">{body}</p>
      {ourMic && (
        <MicHelpSteps className="mt-2 text-xs text-gray-300" />
      )}

      {micLine && (
        <div
          className={`mt-2 rounded-lg px-2 py-1.5 text-xs ring-1 ${
            mic?.ok
              ? "bg-green-950/50 text-green-200 ring-green-900"
              : "bg-red-950/50 text-red-200 ring-red-900"
          }`}
        >
          {micLine}
          {/* the steps are already on screen for our own mic failures */}
          {mic?.reason === "denied" && !ourMic && (
            <MicHelpSteps className="mt-1" />
          )}
        </div>
      )}

      {netUnavailable && !net && (
        <div className="mt-2 text-xs text-gray-400">
          {t("call_check_net_needs_call")}
        </div>
      )}
      {noTurn && !net && (
        <div className="mt-2 text-xs text-gray-400">
          {t("call_check_net_no_turn")}
        </div>
      )}
      {net && (
        <div className="mt-2 rounded-lg bg-gray-900/60 px-2 py-1.5 text-xs ring-1 ring-gray-700">
          {(
            [
              ["STUN", net.stun],
              ["TURN UDP", net.turnUdp],
              ["TURN TCP", net.turnTcp],
              ["TURN TLS", net.turnTls],
            ] as const
          ).map(([label, ok]) => (
            <div
              key={label}
              className="flex items-center justify-between py-0.5"
              data-testid={`net-${label}`}
            >
              <span>{label}</span>
              {ok ? (
                <Check
                  className="h-3.5 w-3.5 text-green-400"
                  aria-label={t("call_check_ok")}
                />
              ) : (
                <X
                  className="h-3.5 w-3.5 text-red-400"
                  aria-label={t("call_check_fail")}
                />
              )}
            </div>
          ))}
          <div className="mt-1 text-gray-300">
            {noTurn
              ? t("call_check_net_no_turn")
              : verdict === "blocked"
              ? t("call_check_net_blocked")
              : verdict === "tls-only"
              ? t("call_check_net_tls_only")
              : verdict === "no-relay"
              ? t("call_check_net_no_relay")
              : t("call_check_net_ok")}
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {canCheckNet && (
          <button
            className={button}
            onClick={() => void checkNet()}
            disabled={netRunning}
          >
            <Wifi className="h-3.5 w-3.5" />
            {netRunning ? t("call_checking") : t("call_check_network")}
          </button>
        )}
        {canCheckMic && (
          <button
            className={button}
            onClick={() => void checkMic()}
            disabled={micRunning}
          >
            <Mic className="h-3.5 w-3.5" />
            {micRunning ? t("call_checking") : t("call_check_mic")}
          </button>
        )}
        {onCallAgain && (
          <button
            className="flex items-center gap-1.5 rounded-lg bg-green-600 px-2.5 py-1.5 text-xs text-white hover:bg-green-500 max-md:min-h-11"
            onClick={onCallAgain}
          >
            <Phone className="h-3.5 w-3.5" />
            {t("call_again")}
          </button>
        )}
        <button className={button} onClick={onClose}>
          {t("close")}
        </button>
      </div>
      <button
        onClick={() => void copyReport()}
        className="mt-2 flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-200 max-md:min-h-11 max-md:text-xs"
      >
        <ClipboardCopy className="h-3 w-3" />
        {t("call_copy_report")}
      </button>
    </div>
  );
}
